import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import {
  IAddPaymentTypeFormData,
  addPaymentTypeFormDefaultValues,
  addPaymentTypeFormSchema,
} from "../models/addPaymentTypeFormData";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  getPaymentOptions,
  sortPaymentOptionsByTypeOrder,
} from "../../../../common/utilities/paymentUtility";
import { PaymentTypes } from "../../../../common/enums";
import { IEventArrivalConfirmationViewModel } from "../../eventArrivalConfirmation/models/eventArrivalConfirmationViewModel";
import { useDepositTenderOption } from "../../common/hooks/useDepositTenderOption";

interface UseAddPaymentTypeFormProps {
  userEventId: number;
  onSuccess: () => void;
}

export const useAddPaymentTypeForm = ({
  userEventId,
  onSuccess,
}: UseAddPaymentTypeFormProps) => {
  const { mutateAsync: createMutation } = useApiMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reuse the same endpoint the arrival page uses — single source of truth for
  // eligible-plans filtering (matches plan-type to event's plan-type restrictions).
  const { data: arrivalInfo, isLoading: isArrivalInfoLoading } =
    useApiQuery<IEventArrivalConfirmationViewModel>({
      queryKey: [
        endpointsMetadata.planner.getEventArrivalConfirmationInfo,
        userEventId,
      ],
      endpoint: `${endpointsMetadata.planner.getEventArrivalConfirmationInfo}?userEventId=${userEventId}`,
      enabled: userEventId > 0,
      retry: false,
    });

  const form = useForm<IAddPaymentTypeFormData>({
    resolver: yupResolver(addPaymentTypeFormSchema),
    defaultValues: addPaymentTypeFormDefaultValues,
  });

  const allPaymentOptions = getPaymentOptions();

  // The studio-configured methods (Deposit is never among them — it joins
  // the list below only under the deposit tender rules).
  const basePaymentOptions = useMemo(() => {
    const filtered = allPaymentOptions.filter((x) =>
      arrivalInfo?.paymentTypes?.some((y) => y === x.id),
    );
    return sortPaymentOptionsByTypeOrder(
      filtered,
      arrivalInfo?.paymentTypes ?? [],
    );
  }, [arrivalInfo]);

  // Initialize the form once we know which payment methods the studio supports,
  // (when present) which subscription plans the user can use for this event,
  // and the event price so the price field is prefilled — mirrors the arrival page.
  // A reservation already stamped PaymentType = Deposit keeps its tender: the
  // Spend row already left the ledger, so the balance no longer covers the
  // price — silently re-tendering it would charge the client twice.
  const isDepositAlreadyTendered =
    Number(arrivalInfo?.selectedPaymentType ?? 0) === PaymentTypes.Deposit;

  // The price the dialog opens with. Normally the event's list price — but a
  // reservation already paid from the deposit must re-submit the amount that
  // was ACTUALLY debited (PricePaid, e.g. a hand-typed 12 on a 20 lv. class),
  // so the BE reconcile delta is zero. Prefilling the list price would ask it
  // to debit the difference a second time (or fail with 31006).
  const alreadyChargedPrice =
    arrivalInfo?.pricePaid === null || arrivalInfo?.pricePaid === undefined
      ? undefined
      : Number(arrivalInfo.pricePaid);
  const prefilledPrice =
    isDepositAlreadyTendered &&
    alreadyChargedPrice !== undefined &&
    Number.isFinite(alreadyChargedPrice)
      ? alreadyChargedPrice
      : arrivalInfo?.eventPrice;

  useEffect(() => {
    form.setValue("userEventId", userEventId);

    if (isDepositAlreadyTendered) {
      // Deposit (900) is never a studio-configured tender, so it can never be
      // in basePaymentOptions — preselect it explicitly.
      form.setValue("paymentTypeId", PaymentTypes.Deposit);
    } else if (basePaymentOptions.length > 0) {
      form.setValue("paymentTypeId", basePaymentOptions[0].id as PaymentTypes);
    }

    if (arrivalInfo) {
      form.setValue("price", prefilledPrice as number);
      form.setValue(
        "subscriptionPlanId",
        arrivalInfo.subscriptions && arrivalInfo.subscriptions.length > 0
          ? Number(arrivalInfo.subscriptions[0].id)
          : null,
      );
    }
  }, [
    userEventId,
    basePaymentOptions,
    arrivalInfo,
    isDepositAlreadyTendered,
    prefilledPrice,
  ]);

  const selectedPaymentTypeId = form.watch("paymentTypeId");
  const watchedPrice = form.watch("price");
  const watchedCurrencyId = form.watch("currencyId");

  // Same deposit tender rules as the arrival-confirmation page: "Депозит"
  // joins the dropdown only when the balance covers the WHOLE price in EUR;
  // it is never preselected, and it submits as a plain paymentTypeId (900).
  const depositTender = useDepositTenderOption({
    depositBalance: arrivalInfo?.depositBalance,
    price: watchedPrice,
    selectedCurrencyId: watchedCurrencyId,
    baseOptions: basePaymentOptions,
    selectedPaymentTypeId,
    isDepositAlreadyTendered,
    onDepositUnavailable: (fallbackPaymentTypeId) =>
      form.setValue("paymentTypeId", fallbackPaymentTypeId as PaymentTypes),
    onDepositRestored: (depositPaymentTypeId) =>
      form.setValue("paymentTypeId", depositPaymentTypeId as PaymentTypes),
  });

  const onSubmit = async (data: IAddPaymentTypeFormData) => {
    setIsSubmitting(true);
    try {
      // Only thread plan id when Plan is selected; only thread price/currency
      // when the BE will actually use them (excludes Plan/Multisport/CoolFit).
      const isPlan = data.paymentTypeId === PaymentTypes.Plan;
      const isTaxBacked =
        data.paymentTypeId === PaymentTypes.Multisport ||
        data.paymentTypeId === PaymentTypes.CoolFit;

      const payload = {
        userEventId: data.userEventId,
        paymentTypeId: data.paymentTypeId,
        hasFilledForm: data.hasFilledForm,
        amount: isPlan || isTaxBacked ? null : data.price,
        currencyId: isPlan || isTaxBacked ? null : data.currencyId,
        subscriptionPlanId: isPlan ? data.subscriptionPlanId : null,
      };

      await createMutation(
        {
          endpoint: endpointsMetadata.planner.addPaymentMethod,
          method: "post",
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
          globalIsLoading: false,
          ...payload,
        },
        {
          onSuccess: () => {
            form.reset();
            onSuccess?.();
          },
        },
      );
    } catch (error) {
      // already handled
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = form.handleSubmit(onSubmit);

  return {
    form,
    // Base options + "Депозит" when the deposit tender is available.
    paymentOptions: depositTender.paymentOptions,
    /**
     * Wire to the payment-method dropdown: a deliberate pick must cancel the
     * pending "restore Депозит when the price comes back down" latch.
     */
    onPaymentTypeUserSelect: depositTender.notifyPaymentTypeSelectedByUser,
    subscriptions: arrivalInfo?.subscriptions ?? [],
    isLoading: isArrivalInfoLoading,
    handleSubmit,
    isSubmitting,
  };
};
